<script setup lang="ts">
import { ref, Ref, computed, ComputedRef } from 'vue';
import { useDisplay } from 'vuetify';
import {
  RobotVisualizer,
  type ShiftVector,
} from '@therobotcompany/robot-visualizer';

import { IRobotFinderResult, ModuleFolderMapping } from '@/models/RobotFinder';
import RobotFinderResult from '@/components/robotFinder/RobotFinderResult.vue';

import { MODULES_BASE_URL } from '@/Constants';
import RobotFinderModuleSelectionPanel from './RobotFinderModuleSelectionPanel.vue';

import gettext from '@/i18n/gettext';
import RobotFinderService from '@/api/RobotFinderApi';
import { ModuleIdNameMapping, RobotFinderType } from '@/models/Robot';
import {
  getModuleIdNameMapping,
  getModuleNamesAsCommaSeparatedString,
} from '@/helpers/RobotHelper';
import RobotFinderApi from '@/api/RobotFinderApi';

const { $gettext } = gettext;

interface Props {
  latestRobotModules: ModuleFolderMapping;
}
const props = defineProps<Props>();

const moduleIds: Ref<string[]> = ref([]);
const selectedKeys = ref<string[]>([]);
const jointAngles: Ref<number[]> = ref([]);

const showSettings = ref(false);
const showGrid = ref(false);
const showAxesHelper = ref(true);
const environmentFileOrUrl: Ref<File | string | undefined> = ref(undefined);
const robotShift: Ref<ShiftVector> = ref([0, 0, 0, 0, 0, 0]);
const environmentShift: Ref<ShiftVector> = ref([0, 0, 0, 0, 0, 0]);
const environmentScale: Ref<number> = ref(1);

const VALID_ROBOT_CONFIGURATION_STRINGS = [
  'BD-L-D-L-E',
  'BD-D-L-D-E',
  'BD-D-L-D-D-E',
  'BD-D-L-D-D-I-E',
  'BD-D-L-D-D-I-D-E',
  'BD-D-L-D-L-D-D-E',
  'BD-D-L-D-D-I-D-D-E',
  'BD-D-L-D-L-D-D-D-E',
  'BD-D-D-I-D-D-I-D-D-D-E',
  'BD-D-L-D-D-I-D-D-I-D-D-E',
];

const selectedModules = computed(() => {
  return selectedKeys.value
    .map((key) => ({
      key,
      module: props.latestRobotModules[key],
    }))
    .filter(Boolean);
});

const display = useDisplay();
const { mdAndUp } = display;
const showInColumns = computed(() => {
  return !mdAndUp.value;
});

const validatedRobotBuild = ref<IRobotFinderResult>();
const isRobotBuildValidated = ref<boolean | undefined>(undefined);

function getPrevNumber(moduleName: string): number | null {
  const numbers = moduleName.match(/\d{2,3}/g);
  if (!numbers || numbers.length === 0) {
    return null;
  }
  if (numbers.length === 1) {
    return parseInt(numbers[0], 10);
  }
  return parseInt(numbers[1], 10); // middle number if multiple
}

function getNextNumberToCompare(moduleName: string): number | null {
  const numbers = moduleName.match(/\d{2,3}/g);
  if (!numbers || numbers.length === 0) {
    return null;
  }
  return parseInt(numbers[0], 10); // always compare the first number
}

const possibleModules = computed(() => {
  const selectedSequence = selectedKeys.value
    .map((k) => props.latestRobotModules[k].moduleName.match(/^[A-Z]+/)![0])
    .join('-');

  const validNextConfigs = VALID_ROBOT_CONFIGURATION_STRINGS.filter((str) =>
    str.startsWith(selectedSequence),
  );

  const nextAllowedTypes = new Set<string>();
  validNextConfigs.forEach((str) => {
    const types = str.split('-');
    nextAllowedTypes.add(types[selectedKeys.value.length]);
  });

  // Determine previous number to match
  let prevNumber: number | null = null;
  if (selectedKeys.value.length > 0) {
    const lastKey = selectedKeys.value[selectedKeys.value.length - 1];
    const lastModuleName = props.latestRobotModules[lastKey]?.moduleName;
    if (lastModuleName) {
      prevNumber = getPrevNumber(lastModuleName);
    }
  }

  return Object.entries(props.latestRobotModules)
    .filter(([_, module]) => {
      const typeMatch = module.moduleName.match(/^[A-Z]+/);
      if (!typeMatch) {
        return false;
      }
      const type = typeMatch[0];

      const firstNumber = getNextNumberToCompare(module.moduleName);
      if (firstNumber === null) {
        return false;
      }

      // Check if first number matches previous number
      if (prevNumber !== null && firstNumber !== prevNumber) {
        return false;
      }

      return nextAllowedTypes.has(type);
    })
    .map(([key, module]) => ({ key, module }));
});

const progress = computed(() => {
  if (selectedKeys.value.length === 0) {
    return 0;
  }

  // Current selected sequence
  const selectedSequence = selectedKeys.value
    .map(
      (k: string) =>
        props.latestRobotModules[k].moduleName.match(/^[A-Z]+/)![0],
    )
    .join('-');

  // Find all valid configs that start with the current sequence
  const validNextConfigs = VALID_ROBOT_CONFIGURATION_STRINGS.filter((str) =>
    str.startsWith(selectedSequence),
  );

  if (validNextConfigs.length === 0) {
    return 0; // invalid sequence
  }

  // Get the shortest remaining valid config (closest to completion)
  const minRemaining = Math.min(
    ...validNextConfigs.map((str) => str.split('-').length),
  );

  const totalSteps = minRemaining;
  const currentSteps = selectedKeys.value.length;

  // Scale to 0–100
  return Math.round((currentSteps / totalSteps) * 100);
});

async function addModule(key: string) {
  selectedKeys.value = [...selectedKeys.value, key];
  moduleIds.value = [...moduleIds.value, key];
  let angles = await RobotFinderApi.getDisplayPose(moduleIds.value.join(','));
  if (angles !== undefined) {
    jointAngles.value = angles;
  } else {
    jointAngles.value = Array(moduleIds.value.length).fill(0);
  }
}

async function removeModule(key: string) {
  const lastIndex = selectedKeys.value.lastIndexOf(key);
  if (lastIndex !== -1) {
    selectedKeys.value = [
      ...selectedKeys.value.slice(0, lastIndex),
      ...selectedKeys.value.slice(lastIndex + 1),
    ];
  }

  const lastModuleIndex = moduleIds.value.lastIndexOf(key);
  if (lastModuleIndex !== -1) {
    moduleIds.value = [
      ...moduleIds.value.slice(0, lastModuleIndex),
      ...moduleIds.value.slice(lastModuleIndex + 1),
    ];

    let angles = await RobotFinderApi.getDisplayPose(moduleIds.value.join(','));
    if (angles !== undefined) {
      jointAngles.value = angles;
    } else {
      jointAngles.value = Array(moduleIds.value.length).fill(0);
    }
  }

  isRobotBuildValidated.value = undefined;
}

const moduleIdNameMapping: ComputedRef<ModuleIdNameMapping[]> = computed(() => {
  return getModuleIdNameMapping(props.latestRobotModules);
});

async function validateRobotBuild() {
  try {
    validatedRobotBuild.value = await RobotFinderService.getRobotFromModuleIds(
      moduleIds.value.map((id) => parseInt(id, 10)),
    );
    if (validatedRobotBuild.value) {
      (validatedRobotBuild.value as IRobotFinderResult).module_names =
        getModuleNamesAsCommaSeparatedString(
          (validatedRobotBuild.value as IRobotFinderResult).module_ids,
          moduleIdNameMapping.value,
        );
      (validatedRobotBuild.value as IRobotFinderResult).resultType =
        RobotFinderType.CUSTOM_CONFIG;
      isRobotBuildValidated.value = true;
    } else {
      isRobotBuildValidated.value = false;
    }
  } catch (err) {
    console.error(`Failed to validate custom config`, err);
    isRobotBuildValidated.value = false;
  }
}
</script>

<template>
  <v-sheet class="robot-finder-builder-wrapper" color="transparent">
    <v-row class="fill-height" align="start" dense>
      <!-- Left panel -->
      <v-col
        v-if="possibleModules.length != 0"
        cols="12"
        md="3"
        class="left-panel"
      >
        <robot-finder-module-selection-panel
          :modules="possibleModules"
          :title="$gettext('Modules Inventory')"
          :search-label="$gettext('Search Modules')"
          :showSearch="true"
          :showDelete="false"
          @on-click="addModule"
        />
      </v-col>

      <v-col
        v-if="moduleIds.length != 0 && !isRobotBuildValidated"
        cols="12"
        md="3"
        class="left-panel"
      >
        <robot-finder-module-selection-panel
          :modules="selectedModules"
          :title="$gettext('Selected Modules')"
          :showSearch="false"
          :showDelete="true"
          :showAlert="progress !== 100 && possibleModules.length === 0"
          :alertMessage="
            $gettext(
              'Uhh oh! You managed to select an invalid sequence of modules. Please remove some modules and try again.',
            )
          "
          :group-by-type="false"
          @on-click="removeModule"
        />
      </v-col>

      <!-- Right panel -->
      <v-col
        class="right-panel"
        :class="
          selectedModules.length !== 0
            ? ''
            : 'd-flex flex-column justify-center align-center'
        "
        cols="12"
        :md="
          isRobotBuildValidated
            ? 12
            : possibleModules.length === 0 || selectedModules.length === 0
              ? 9
              : 6
        "
        style="position: relative; height: 100%"
      >
        <v-card
          v-if="moduleIds.length == 0"
          class="dashed-sheet"
          rounded="xl"
          variant="flat"
          width="300px"
        >
          <v-card-title class="text-center">
            <v-icon color="info" icon="mdi-robot-industrial"></v-icon>
          </v-card-title>
          <v-card-text class="text-center">
            {{
              $gettext(
                'Every RobCo Robot begins with a Base Drive module. Choose a Base Drive and start building!',
              )
            }}
          </v-card-text>
        </v-card>
        <div
          v-if="moduleIds.length != 0 && !isRobotBuildValidated"
          class="pa-4"
          rounded="xl"
          style="width: 100%; height: 100%"
        >
          <v-progress-linear
            class="mb-3"
            :height="30"
            :bg-opacity="0.3"
            :model-value="progress"
            color="primary"
            rounded
          >
            <template v-slot:default="{ value: progress }">
              <span class="mdi mdi-hammer-wrench mr-2"></span>
              {{ $gettext('Builder Progress:') }} {{ Math.ceil(progress) }}%
            </template>
          </v-progress-linear>
          <div class="visualizer-wrapper">
            <robot-visualizer
              ref="visualizer-builder"
              v-model:environmentScale="environmentScale"
              :modulesBaseUrl="MODULES_BASE_URL"
              :moduleIds="moduleIds"
              :currentAngles="jointAngles"
              :showGrid="showGrid"
              :showAxesHelper="showAxesHelper"
              :tcpShift="[0, 0, 0, 0, 0, 0]"
              :showSettings="showSettings"
              :environmentShift="environmentShift"
              :robotShift="robotShift"
              backgroundColor="#fff"
              :environmentFileOrUrl="environmentFileOrUrl"
              :demoAnimationOnHover="true"
            />
          </div>
          <!-- Overlay button at bottom center -->
          <div class="d-flex flex-column validation-action-overlay">
            <v-alert
              v-if="isRobotBuildValidated === false"
              class="ma-3"
              type="error"
              outlined
            >
              <template #prepend>
                <span class="mdi mdi-robot-confused"></span>
              </template>
              {{
                $gettext(
                  'We ran simulations and your sequence of modules could not be validated. Please select a different sequence of modules and try again.',
                )
              }}
            </v-alert>
            <v-btn
              v-if="progress == 100"
              class="gradient-sunrise-apricot glow"
              :class="
                isRobotBuildValidated === false ? 'disabled' : 'bouncy-button'
              "
              style="width: 250px"
              @click="validateRobotBuild"
            >
              {{ $gettext('Validate Build') }}
            </v-btn>
          </div>
        </div>
        <v-sheet
          v-if="
            moduleIds.length != 0 &&
            isRobotBuildValidated &&
            validatedRobotBuild != undefined
          "
          class="pa-4"
          rounded="xl"
          style="width: 100%; min-width: 0"
        >
          <robot-finder-result
            :data="validatedRobotBuild"
            :name="$gettext('Your Custom Robot Configuration')"
            :show-in-columns="showInColumns"
          />
        </v-sheet>
        <v-btn
          v-if="progress == 100 && isRobotBuildValidated"
          class="gradient-sunrise-apricot mt-4 ml-1 mb-1"
          @click="() => (isRobotBuildValidated = undefined)"
        >
          <span class="mdi mdi-keyboard-backspace mr-2"></span>
          {{ $gettext('Modify Build') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<style lang="scss" scoped>
.robot-finder-builder-wrapper {
  @media (min-width: 960px) {
    height: calc(100vh - 160px);
    width: 100%;

    .visualizer-wrapper {
      height: calc(100% - 41px);
    }
    .left-panel {
      width: 100%;
      height: 100%;
    }
    .validation-action-overlay {
      bottom: 24px !important;
    }
  }

  @media (max-width: 959px) {
    .visualizer-wrapper {
      height: 400px;
    }

    .dashed-sheet {
      margin-top: 24px;
      margin-bottom: 24px;
    }
  }

  .dashed-sheet {
    border: 2px dashed;
    background-color: rgb(var(--v-theme-surface));
  }

  .validation-action-overlay {
    position: absolute;
    color: rgb(var(--v-theme-on-primary));
    bottom: 48px; /* distance from bottom of panel */
    left: 50%; /* center horizontally */
    transform: translateX(-50%);
    align-items: center;
    z-index: 2051;
  }
  .visualizer-wrapper {
    background-color: white;
    outline: 8px solid white;
    border-radius: 16px;
    margin: 24px 8px;
  }
}
</style>
